/**
 * Shared field-apply logic for AI domain enrichment.
 *
 * The enrich-domain SSE pipeline returns a `DomainEnrichmentResult`. Turning that
 * into organization fields — which key maps to which column, what counts as an
 * "empty" value, uploading the logo, resolving the tier — is identical whether
 * we're creating a company (QuickCreate / OrganizationForm) or enriching an
 * existing one (OrganizationEnrichDialog). It lives here once instead of being
 * duplicated at each call site.
 */
import { fileApi } from '@/shared/files/services/fileApi';
import type {
  DomainEnrichmentResult,
  Organization,
  OrganizationUpdate,
  Tier,
} from '../types';

/** Selection key for the logo row (handled out-of-band because it needs an async upload). */
export const ENRICH_LOGO_KEY = 'logo';

export type EnrichFieldKind = 'scalar' | 'multi';

export interface EnrichFieldDef {
  /** Stable key used in the review UI + the selection set. */
  key: string;
  /** Key on DomainEnrichmentResult holding the proposed value. */
  sourceKey: keyof DomainEnrichmentResult;
  /** Column on the organization (and OrganizationUpdate) the value applies to. */
  orgField: keyof Organization & keyof OrganizationUpdate;
  kind: EnrichFieldKind;
  /** i18n key (contacts namespace) for the field label. */
  labelKey: string;
}

/**
 * The enrichable fields, in review-display order. `logo` is intentionally NOT
 * here — it needs an async upload before it becomes a `logo_file_id`, so the
 * dialog applies it separately (see {@link uploadEnrichmentLogo}).
 */
export const ENRICH_FIELD_DEFS: readonly EnrichFieldDef[] = [
  { key: 'industry', sourceKey: 'industry', orgField: 'industry', kind: 'scalar', labelKey: 'organizations.industry' },
  { key: 'employee_count', sourceKey: 'employee_count', orgField: 'employee_count', kind: 'scalar', labelKey: 'organizations.employees' },
  { key: 'description', sourceKey: 'description', orgField: 'description', kind: 'scalar', labelKey: 'organizations.description' },
  { key: 'website', sourceKey: 'website', orgField: 'website', kind: 'scalar', labelKey: 'organizations.website' },
  { key: 'linkedin_url', sourceKey: 'linkedin_url', orgField: 'linkedin_url', kind: 'scalar', labelKey: 'common:social.linkedIn' },
  { key: 'country', sourceKey: 'country', orgField: 'country', kind: 'scalar', labelKey: 'organizations.country' },
  { key: 'city', sourceKey: 'city', orgField: 'city', kind: 'scalar', labelKey: 'organizations.city' },
  // Legal entity type. Both the SSE payload key and the persisted org column are
  // `organization_type` (NOT the stale `company_type` alias the API ignores).
  { key: 'organization_type', sourceKey: 'organization_type', orgField: 'organization_type', kind: 'scalar', labelKey: 'common:businessClassification.companyLegalType.label' },
  { key: 'business_models', sourceKey: 'business_models', orgField: 'business_models', kind: 'multi', labelKey: 'common:businessClassification.businessModel.label' },
  // Use a distinct label ("Industry Verticals") — the shared industryVertical.label
  // is just "Industry", which would collide with the `industry` row above.
  { key: 'industry_verticals', sourceKey: 'industry_verticals', orgField: 'industry_verticals', kind: 'multi', labelKey: 'organizations.industryVerticals' },
] as const;

/** Strip protocol, path, port, and a leading www. from a domain-ish string. */
export function cleanDomainInput(value: string): string {
  let d = value.trim().toLowerCase();
  if (d.startsWith('http://') || d.startsWith('https://')) {
    d = d.split('://', 2)[1] ?? '';
  }
  d = d.split('/', 1)[0];
  d = d.split(':', 1)[0];
  return d.replace(/^www\./, '');
}

/**
 * The domain to enrich an existing org against: its stored `domain`, else a
 * domain derived from its `website`. Empty when the org has neither (the caller
 * then prompts the user for one).
 */
export function resolveEnrichmentDomain(organization: Organization): string {
  if (organization.domain && organization.domain.trim()) {
    return cleanDomainInput(organization.domain);
  }
  if (organization.website && organization.website.trim()) {
    return cleanDomainInput(organization.website);
  }
  return '';
}

/** A value that carries no information — null, undefined, blank string, or empty array. */
export function isEmptyEnrichValue(value: unknown): boolean {
  if (value === null || value === undefined) return true;
  if (typeof value === 'string') return value.trim() === '';
  if (Array.isArray(value)) return value.length === 0;
  return false;
}

/** The proposed value for a field, normalized (trimmed string / array / null). */
export function proposedEnrichValue(
  result: DomainEnrichmentResult,
  def: EnrichFieldDef,
): string | string[] | null {
  const raw = result[def.sourceKey];
  if (def.kind === 'multi') {
    return Array.isArray(raw) && raw.length > 0 ? raw : null;
  }
  const str = typeof raw === 'string' ? raw.trim() : '';
  return str === '' ? null : str;
}

/**
 * Whether two field values are equal (so the review can skip a no-op row).
 * Arrays compare order-independently; scalars/null compare by identity.
 */
export function sameEnrichValue(
  a: string | string[] | null,
  b: string | string[] | null,
): boolean {
  if (Array.isArray(a) && Array.isArray(b)) {
    return a.length === b.length && a.every((v) => b.includes(v));
  }
  return a === b;
}

/** The organization's current value for a field. */
export function currentEnrichValue(
  organization: Organization,
  def: EnrichFieldDef,
): string | string[] | null {
  const raw = organization[def.orgField];
  if (def.kind === 'multi') {
    return Array.isArray(raw) && raw.length > 0 ? (raw as string[]) : null;
  }
  return typeof raw === 'string' && raw.trim() !== '' ? raw : null;
}

/**
 * The default field selection: fill only fields the org is missing. A field is
 * pre-checked when enrichment proposed something AND the org's value is empty —
 * a populated field is never overwritten unless the user explicitly ticks it.
 * The logo follows the same rule (checked only when the org has no logo yet).
 */
export function defaultSelectedEnrichFields(
  organization: Organization,
  result: DomainEnrichmentResult,
): Set<string> {
  const selected = new Set<string>();
  for (const def of ENRICH_FIELD_DEFS) {
    const proposed = proposedEnrichValue(result, def);
    if (proposed === null) continue;
    if (isEmptyEnrichValue(currentEnrichValue(organization, def))) {
      selected.add(def.key);
    }
  }
  if (result.logo_url && !organization.logo_file_id) {
    selected.add(ENRICH_LOGO_KEY);
  }
  return selected;
}

/**
 * Build the organization update from the user-approved scalar/array fields.
 * Excludes the logo (applied separately via {@link uploadEnrichmentLogo}) and
 * any field whose proposal is empty.
 */
export function buildEnrichmentOrgUpdate(
  result: DomainEnrichmentResult,
  selectedKeys: ReadonlySet<string>,
): OrganizationUpdate {
  const update: OrganizationUpdate = {};
  for (const def of ENRICH_FIELD_DEFS) {
    if (!selectedKeys.has(def.key)) continue;
    const proposed = proposedEnrichValue(result, def);
    if (proposed === null) continue;
    // Each def's orgField is a known OrganizationUpdate key with a compatible
    // value type (scalar → string column, multi → string[] column).
    (update as Record<string, string | string[]>)[def.orgField] = proposed;
  }
  return update;
}

/**
 * Upload an enriched logo URL as a file and return its id. Returns undefined if
 * the upload fails — enrichment should never block on a missing logo.
 */
export async function uploadEnrichmentLogo(
  logoUrl: string,
  companyName: string,
): Promise<string | undefined> {
  try {
    const result = await fileApi.uploadFromUrl({
      url: logoUrl,
      filename: `${companyName || 'company'}-logo.png`,
      description: `Logo for ${companyName}`,
      is_public: true,
      is_user_file: false,
    });
    return result.data.id;
  } catch {
    // Non-actionable: the caller proceeds without a logo.
    return undefined;
  }
}

/** Resolve an enriched tier name (e.g. "Enterprise") to a tenant tier id. */
export function resolveEnrichmentTierId(
  tierName: string | null | undefined,
  tiers: Tier[] | undefined,
): string | undefined {
  if (!tierName || !tiers) return undefined;
  const lower = tierName.toLowerCase();
  return tiers.find((tier) => tier.name.toLowerCase() === lower)?.id;
}
